// Mock weather grid for windy-style visualization
// Coverage: VN + East Sea, ~ 102-114°E × 8-23.5°N, resolution 0.5°

export const GRID_BOUNDS = {
  west: 102,
  east: 115,
  south: 7,
  north: 24,
}

export const GRID_RES = 0.5
export const GRID_W = Math.round((GRID_BOUNDS.east - GRID_BOUNDS.west) / GRID_RES) + 1 // 27
export const GRID_H = Math.round((GRID_BOUNDS.north - GRID_BOUNDS.south) / GRID_RES) + 1 // 35

export interface WeatherFrame {
  u: Float32Array // east-west wind component (m/s)
  v: Float32Array // north-south wind component (m/s)
  temp: Float32Array // °C
  precip: Float32Array // mm/h
  pressure: Float32Array // hPa
}

function fbm(x: number, y: number, t: number): number {
  let sum = 0
  let amp = 1
  let freq = 0.4
  for (let i = 0; i < 4; i++) {
    sum += amp * Math.sin(x * freq + t * 0.7 + i) * Math.cos(y * freq * 1.3 - t * 0.5 + i * 1.7)
    amp *= 0.5
    freq *= 2
  }
  return sum
}

export function generateFrame(t: number): WeatherFrame {
  const total = GRID_W * GRID_H
  const u = new Float32Array(total)
  const v = new Float32Array(total)
  const temp = new Float32Array(total)
  const precip = new Float32Array(total)
  const pressure = new Float32Array(total)

  for (let j = 0; j < GRID_H; j++) {
    for (let i = 0; i < GRID_W; i++) {
      const lon = GRID_BOUNDS.west + i * GRID_RES
      const lat = GRID_BOUNDS.south + j * GRID_RES

      // Base trade winds: easterly in tropics, westerly higher latitudes
      const baseU = -8 + lat * 0.4 + fbm(lon * 0.3, lat * 0.3, t) * 6
      const baseV = -2 + Math.sin(lat * 0.3 + t * 0.3) * 3 + fbm(lon * 0.2 + 5, lat * 0.2, t * 0.6) * 5

      // Cyclonic feature in East Sea
      const cycLon = 110 + Math.sin(t * 0.3) * 1.5
      const cycLat = 14 + Math.cos(t * 0.3) * 2
      const dx = lon - cycLon
      const dy = lat - cycLat
      const r = Math.sqrt(dx * dx + dy * dy)
      const intensity = Math.exp(-(r * r) / 6) * 18
      const cycU = -dy * intensity / Math.max(r, 0.5)
      const cycV = dx * intensity / Math.max(r, 0.5)

      const idx = j * GRID_W + i
      u[idx] = baseU + cycU
      v[idx] = baseV + cycV

      // Temperature: warmer south, cooler north + diurnal
      temp[idx] = 32 - lat * 0.6 + fbm(lon * 0.2, lat * 0.2, t * 0.2) * 4 + Math.sin(t * 0.1) * 2

      // Precipitation: high at cyclone center
      precip[idx] = Math.max(0, intensity * 0.8 + fbm(lon, lat, t) * 2 - 1)

      // Pressure: low at cyclone, normal around 1010
      pressure[idx] = 1010 - intensity * 1.8 + fbm(lon * 0.5, lat * 0.5, t * 0.3) * 4
    }
  }

  return { u, v, temp, precip, pressure }
}

export function bilinearSample(frame: WeatherFrame, lon: number, lat: number, field: 'u' | 'v' | 'temp' | 'precip' | 'pressure'): number {
  const fi = (lon - GRID_BOUNDS.west) / GRID_RES
  const fj = (lat - GRID_BOUNDS.south) / GRID_RES
  if (fi < 0 || fj < 0 || fi >= GRID_W - 1 || fj >= GRID_H - 1) return 0
  const i0 = Math.floor(fi)
  const j0 = Math.floor(fj)
  const i1 = i0 + 1
  const j1 = j0 + 1
  const di = fi - i0
  const dj = fj - j0
  const arr = frame[field]
  const v00 = arr[j0 * GRID_W + i0]
  const v10 = arr[j0 * GRID_W + i1]
  const v01 = arr[j1 * GRID_W + i0]
  const v11 = arr[j1 * GRID_W + i1]
  return (1 - di) * (1 - dj) * v00 + di * (1 - dj) * v10 + (1 - di) * dj * v01 + di * dj * v11
}
